// RegulatoryCompliance.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// CKEditor Imports
// import { CKEditor } from "@ckeditor/ckeditor5-react";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  listBioMedicalWasteReportCMS,
  addBioMedicalWasteReportCMS,
  editBioMedicalWasteReportCMS,
  updateBioMedicalWasteReportCMS,
  trashBioMedicalWasteReportCMS,
  // Assuming this adapter is available for PDF upload
  addUpdateBmwPdfFileCMS, 
  listOrganTransplantReportCMS,
  addOrganTransplantReportCMS,
  editOrganTransplantReportCMS,
  updateOrganTransplantReportCMS,
  trashOrganTransplantReportCMS,
  listOrganTransplantCommitteeCMS,
  addOrganTransplantCommitteeCMS,
  editOrganTransplantCommitteeCMS,
  updateOrganTransplantCommitteeCMS,
  trashOrganTransplantCommitteeCMS,
} from "../../Adapters/ApiAdapter";
import CKEditorField from "../../components/CKEditorField";
import { baseUrl } from "../../variables/Variables";

// Helper function to format date from yyyy-mm-dd to dd-mm-yyyy for display
const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return "";
  // If the date is in yyyy-mm-dd format, reverse it
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

// Helper function to render image upload fields (reusable logic)
const ImageUploadField = ({ label, fieldName, state, setState }) => (
  <Box display="flex" alignItems="center" my={1} flexWrap="wrap">
    <Button variant="contained" component="label" sx={{ minWidth: '150px' }}>
      Upload {label}
      <input
        type="file"
        accept="image/*"
        hidden
        // Ensure that when editing, the input stores a new File object or null, not the old string path.
        onChange={(e) =>
          setState({ ...state, [fieldName]: e.target.files[0] })
        }
      />
    </Button>
    <Typography variant="body2" sx={{ ml: 2 }}>
      {state[fieldName] instanceof File
        ? state[fieldName].name // New file selected
        : state[fieldName] && state[fieldName].includes('/') // Check if it's a URL/path from DB
          ? 'File already exists' 
          : state[fieldName] || "No file selected"}
    </Typography>
  </Box>
);

// Helper component for SEO fields (reusable for both forms)
// Takes 'data' and 'setData' props dynamically
const SeoFormFields = ({ data, setData }) => (
  <>
    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
      SEO & Social Media
    </Typography>

    <TextField
      fullWidth
      label="SEO Title"
      margin="dense"
      value={data.seo_title || ""}
      onChange={(e) => setData({ ...data, seo_title: e.target.value })}
    />
    <TextField
      fullWidth
      label="Meta Title"
      margin="dense"
      value={data.meta_title || ""}
      onChange={(e) => setData({ ...data, meta_title: e.target.value })}
    />

    <Typography variant="body1" sx={{ mt: 2 }}>
      Meta Description (Content)
    </Typography>
    <Box sx={{ ".ck-editor__editable_inline": { minHeight: "150px" } }}>
      {/* <CKEditor
        editor={ClassicEditor}
        data={data.meta_description || ""}
        onChange={(event, editor) => {
          const content = editor.getData();
          setData({ ...data, meta_description: content });
        }}
        config={{
          toolbar: [
            "heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "undo", "redo"
          ],
        }}
      /> */}
      <CKEditorField
            value={data.meta_description}
            onChange={(data) => setData({ ...data, meta_description: data })}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=regulatory_compliance`}
          />
    </Box>

    <TextField
      fullWidth
      label="Meta Keywords"
      margin="dense"
      value={data.meta_keywords || ""}
      onChange={(e) => setData({ ...data, meta_keywords: e.target.value })}
    />
    <TextField
      fullWidth
      label="Custom Scripts"
      margin="dense"
      multiline
      rows={3}
      value={data.custom_scripts || ""}
      onChange={(e) => setData({ ...data, custom_scripts: e.target.value })}
    />
    <TextField
      fullWidth
      label="Canonical Link"
      margin="dense"
      value={data.canonical_link || ""}
      onChange={(e) => setData({ ...data, canonical_link: e.target.value })}
    />
    <TextField
      fullWidth
      label="Article Modified Time (e.g., YYYY-MM-DDTHH:mm:ss)"
      margin="dense"
      value={data.article_modified_time || ""}
      onChange={(e) => setData({ ...data, article_modified_time: e.target.value })}
    />

    {/* Open Graph (OG) Fields */}
    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
      Open Graph (Social Sharing)
    </Typography>
    <TextField
      fullWidth
      label="OG Title"
      margin="dense"
      value={data.og_title || ""}
      onChange={(e) => setData({ ...data, og_title: e.target.value })}
    />
    <TextField
      fullWidth
      label="OG Description"
      margin="dense"
      value={data.og_description || ""}
      onChange={(e) => setData({ ...data, og_description: e.target.value })}
    />
    <TextField
      fullWidth
      label="OG URL"
      margin="dense"
      value={data.og_url || ""}
      onChange={(e) => setData({ ...data, og_url: e.target.value })}
    />
    <TextField
      label="OG Type (e.g., article)"
      margin="dense"
      value={data.og_type || ""}
      onChange={(e) => setData({ ...data, og_type: e.target.value })}
      sx={{ mr: 2, width: '48%' }}
    />
    <TextField
      label="OG Locale"
      margin="dense"
      value={data.og_locale || ""}
      onChange={(e) => setData({ ...data, og_locale: e.target.value })}
      sx={{ width: '48%' }}
    />
    <TextField
      label="OG Site Name"
      margin="dense"
      value={data.og_site_name || ""}
      onChange={(e) => setData({ ...data, og_site_name: e.target.value })}
      sx={{ mr: 2, width: '48%' }}
    />
    <TextField
      label="OG Image Type (e.g., image/png)"
      margin="dense"
      value={data.og_image_type || ""}
      onChange={(e) => setData({ ...data, og_image_type: e.target.value })}
      sx={{ width: '48%' }}
    />
    <TextField
      label="OG Image Width"
      margin="dense"
      type="number"
      value={data.og_image_width || ""}
      onChange={(e) => setData({ ...data, og_image_width: e.target.value })}
      sx={{ mr: 2, width: '48%' }}
    />
    <TextField
      label="OG Image Height"
      margin="dense"
      type="number"
      value={data.og_image_height || ""}
      onChange={(e) => setData({ ...data, og_image_height: e.target.value })}
      sx={{ width: '48%' }}
    />

    <ImageUploadField 
      label="OG Image" 
      fieldName="og_image" 
      state={data} 
      setState={setData} 
    />

    {/* Twitter Fields */}
    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
      Twitter Cards
    </Typography>
    <TextField
      fullWidth
      label="Twitter Card (e.g., summary_large_image)"
      margin="dense"
      value={data.twitter_card || ""}
      onChange={(e) => setData({ ...data, twitter_card: e.target.value })}
    />
    <TextField
      fullWidth
      label="Twitter Title"
      margin="dense"
      value={data.twitter_title || ""}
      onChange={(e) => setData({ ...data, twitter_title: e.target.value })}
    />
    <TextField
      fullWidth
      label="Twitter Description"
      margin="dense"
      value={data.twitter_description || ""}
      onChange={(e) => setData({ ...data, twitter_description: e.target.value })}
    />
    <TextField
      fullWidth
      label="Twitter Site (@username)"
      margin="dense"
      value={data.twitter_site || ""}
      onChange={(e) => setData({ ...data, twitter_site: e.target.value })}
    />
    <ImageUploadField 
      label="Twitter Image" 
      fieldName="twitter_image" 
      state={data} 
      setState={setData} 
    />
  </>
);

// Helper component for BMW form fields (reusable for Add and Edit)
const BmwFormFields = ({ data, setData }) => (
    <>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Waste Details
        </Typography>
        <TextField
            fullWidth
            label="Date"
            type="date"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={data.date || ""}
            onChange={(e) => setData({ ...data, date: e.target.value })}
        />
        {/* Blue */}
        <TextField
            label="Blue Bags"
            margin="dense"
            type="number"
            value={data.blue_bags || ""}
            onChange={(e) => setData({ ...data, blue_bags: e.target.value })}
            sx={{ mr: 2, width: "48%" }}
        />
        <TextField
            label="Blue Weight (kg)"
            margin="dense"
            type="number"
            value={data.blue_weight || ""}
            onChange={(e) => setData({ ...data, blue_weight: e.target.value })}
            sx={{ width: "48%" }}
        />

        {/* Red */}
        <TextField
            label="Red Bags"
            margin="dense"
            type="number"
            value={data.red_bags || ""}
            onChange={(e) => setData({ ...data, red_bags: e.target.value })}
            sx={{ mr: 2, width: "48%" }}
        />
        <TextField
            label="Red Weight (kg)"
            margin="dense"
            type="number"
            value={data.red_weight || ""}
            onChange={(e) => setData({ ...data, red_weight: e.target.value })}
            sx={{ width: "48%" }}
        />

        {/* White */}
        <TextField
            label="White Bags"
            margin="dense"
            type="number"
            value={data.white_bags || ""}
            onChange={(e) => setData({ ...data, white_bags: e.target.value })}
            sx={{ mr: 2, width: "48%" }}
        />
        <TextField
            label="White Weight (kg)"
            margin="dense"
            type="number"
            value={data.white_weight || ""}
            onChange={(e) => setData({ ...data, white_weight: e.target.value })}
            sx={{ width: "48%" }}
        />

        {/* Yellow */}
        <TextField
            label="Yellow Bags"
            margin="dense"
            type="number"
            value={data.yellow_bags || ""}
            onChange={(e) => setData({ ...data, yellow_bags: e.target.value })}
            sx={{ mr: 2, width: "48%" }}
        />
        <TextField
            label="Yellow Weight (kg)"
            margin="dense"
            type="number"
            value={data.yellow_weight || ""}
            onChange={(e) => setData({ ...data, yellow_weight: e.target.value })}
            sx={{ width: "48%" }}
        />

        {/* Cytotoxic Yellow */}
        <TextField
            label="Cytotoxic Yellow Bags"
            margin="dense"
            type="number"
            value={data.cytotoxic_yellow_bags || ""}
            onChange={(e) => setData({ ...data, cytotoxic_yellow_bags: e.target.value })}
            sx={{ mr: 2, width: "48%" }}
        />
        <TextField
            label="Cytotoxic Yellow Weight (kg)"
            margin="dense"
            type="number"
            value={data.cytotoxic_yellow_weight || ""}
            onChange={(e) => setData({ ...data, cytotoxic_yellow_weight: e.target.value })}
            sx={{ width: "48%" }}
        />

        {/* COVID */}
        <TextField
            label="COVID Bags"
            margin="dense"
            type="number"
            value={data.covid_bags || ""}
            onChange={(e) => setData({ ...data, covid_bags: e.target.value })}
            sx={{ mr: 2, width: "48%" }}
        />
        <TextField
            label="COVID Weight (kg)"
            margin="dense"
            type="number"
            value={data.covid_weight || ""}
            onChange={(e) => setData({ ...data, covid_weight: e.target.value })}
            sx={{ width: "48%" }}
        />

        {/* Sharp */}
        <TextField
            label="Sharp Bags"
            margin="dense"
            type="number"
            value={data.sharp_bags || ""}
            onChange={(e) => setData({ ...data, sharp_bags: e.target.value })}
            sx={{ mr: 2, width: "48%" }}
        />
        <TextField
            label="Sharp Weight (kg)"
            margin="dense"
            type="number"
            value={data.sharp_weight || ""}
            onChange={(e) => setData({ ...data, sharp_weight: e.target.value })}
            sx={{ width: "48%" }}
        />
        <SeoFormFields data={data} setData={setData} />
    </>
);

// Helper component for OT form fields (reusable for Add and Edit)
const OtFormFields = ({ data, setData }) => (
    <>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Transplant Details
        </Typography>
        <TextField
            fullWidth
            label="Date"
            type="date"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={data.date || ""}
            onChange={(e) => setData({ ...data, date: e.target.value })}
        />
        <TextField
            fullWidth
            label="UHID of Recipient"
            margin="dense"
            value={data.uhid_recipient || ""}
            onChange={(e) => setData({ ...data, uhid_recipient: e.target.value })}
        />
        <TextField
            fullWidth
            label="Organ"
            margin="dense"
            value={data.organ || ""}
            onChange={(e) => setData({ ...data, organ: e.target.value })}
        />
        <TextField
            fullWidth
            label="Donor Type"
            margin="dense"
            value={data.donor_type || ""}
            onChange={(e) => setData({ ...data, donor_type: e.target.value })}
        />
        <TextField
            fullWidth
            label="Results"
            margin="dense"
            value={data.results || ""}
            onChange={(e) => setData({ ...data, results: e.target.value })}
        />
        <SeoFormFields data={data} setData={setData} />
    </>
);

// Suggestions for the Committee form (any other value can still be typed in)
const COMMITTEE_TYPE_OPTIONS = [
    "Competent Authority",
    "Divisional Authorization Committee",
    "State Level Authorisation Committee",
    "State Authorisation Committee",
    "District Authorization Committee",
    "Local Authorization Committee",
    "Hospital Based Committee",
    "Hospital Based Competent Authority Committee",
];
const COMMITTEE_RESULT_OPTIONS = ["Approved", "Pending", "Rejected"];

const EMPTY_COMMITTEE = {
    hospital: "S L Raheja Hospital, Mumbai",
    date: "",
    committee_type: "",
    code_no: "",
    results: "",
};

// Helper component for Committee form fields (reusable for Add and Edit)
const CommitteeFormFields = ({ data, setData }) => (
    <>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Committee Details
        </Typography>
        <TextField
            fullWidth
            label="Hospital"
            margin="dense"
            value={data.hospital || ""}
            onChange={(e) => setData({ ...data, hospital: e.target.value })}
        />
        <TextField
            fullWidth
            required
            label="Date"
            type="date"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={data.date || ""}
            onChange={(e) => setData({ ...data, date: e.target.value })}
        />
        <Autocomplete
            freeSolo
            options={COMMITTEE_TYPE_OPTIONS}
            value={data.committee_type || null}
            onChange={(_, value) => setData({ ...data, committee_type: value || "" })}
            inputValue={data.committee_type || ""}
            onInputChange={(_, value) => setData({ ...data, committee_type: value })}
            renderInput={(params) => (
                <TextField {...params} required label="Type of Committee" margin="dense" />
            )}
        />
        <TextField
            fullWidth
            required
            label="Code No Assigned to the Case"
            margin="dense"
            value={data.code_no || ""}
            onChange={(e) => setData({ ...data, code_no: e.target.value })}
        />
        <Autocomplete
            freeSolo
            options={COMMITTEE_RESULT_OPTIONS}
            value={data.results || null}
            onChange={(_, value) => setData({ ...data, results: value || "" })}
            inputValue={data.results || ""}
            onInputChange={(_, value) => setData({ ...data, results: value })}
            renderInput={(params) => (
                <TextField {...params} required label="Results" margin="dense" />
            )}
        />
    </>
);


const RegulatoryCompliance = () => {
  const [tab, setTab] = useState(0);
  const handleTabChange = (_, newValue) => setTab(newValue);

  const [loadingAction, setLoadingAction] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // ---------------------- BIO-MEDICAL WASTE ----------------------
  const [bmwReports, setBmwReports] = useState([]);
  const [bmwLoading, setBmwLoading] = useState(true);
  const [bmwError, setBmwError] = useState(null);
  // State for EDITING (will hold the object being edited, null when not editing)
  const [bmwEdit, setBmwEdit] = useState(null); 
  const [bmwAddOpen, setBmwAddOpen] = useState(false);
  
  // State for PDF UPLOAD
  const [bmwPdfUploadOpen, setBmwPdfUploadOpen] = useState(false);
  const [bmwPdfData, setBmwPdfData] = useState({ 
    year: new Date().getFullYear().toString(), 
    name: `Annual Bio-Medical Waste Report - ${new Date().getFullYear().toString()}`, 
    pdf_file: null 
  });

  // State for ADDING new report (reset after save)
  const [newBMW, setNewBMW] = useState({
    date: "",
    blue_bags: "",
    blue_weight: "",
    red_bags: "",
    red_weight: "",
    white_bags: "",
    white_weight: "",
    yellow_bags: "",
    yellow_weight: "",
    cytotoxic_yellow_bags: "",
    cytotoxic_yellow_weight: "",
    covid_bags: "",
    covid_weight: "",
    sharp_bags: "",
    sharp_weight: "",
    seo_title: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    custom_scripts: "",
    og_title: "",
    og_description: "",
    og_type: "",
    og_url: "",
    og_image: null,
    og_locale: "",
    og_site_name: "",
    og_image_width: "",
    og_image_height: "",
    og_image_type: "",
    article_modified_time: "",
    canonical_link: "",
    twitter_card: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: null,
    twitter_site: "",
  });

  useEffect(() => {
    const fetchBMW = async () => {
      try {
        const res = await listBioMedicalWasteReportCMS();
        if (res?.data) setBmwReports(res.data);
        else setBmwError("Failed to fetch Bio-Medical Waste Reports");
      } catch {
        setBmwError("Error fetching reports.");
      } finally {
        setBmwLoading(false);
      }
    };
    fetchBMW();
  }, []);

  // Handler for text/data report add/update
  const handleBMWAddOrUpdate = async (id = null) => {
    setLoadingAction(true);
    const formData = new FormData();
    // Use bmwEdit data if ID is present (editing), otherwise use newBMW (adding)
    const data = id ? bmwEdit : newBMW;

    Object.entries(data).forEach(([key, val]) => {
      if (key === 'og_image' || key === 'twitter_image') {
        // Only append file if it is a File object (for new upload)
        if (val instanceof File) {
          formData.append(key, val);
        } else if (typeof val === 'string' && val.includes('/')) {
            // If it's a string path (from the DB), don't append to FormData for file upload, 
            // but keep the key/value for the backend to handle updates without re-uploading the image.
             formData.append(key, val);
        }
      } else {
        // Append all other fields, handling null/undefined
        formData.append(key, val || "");
      }
    });

    try {
      const res = id
        ? await updateBioMedicalWasteReportCMS(data.id, formData) // Use data.id if editing
        : await addBioMedicalWasteReportCMS(formData);
      if (res?.status === "true" || res?.success) {
        toast.success(
          id ? "Report updated successfully!" : "Report added successfully!"
        );
        // Refresh list
        const updated = await listBioMedicalWasteReportCMS();
        setBmwReports(updated.data || []);
        
        // Close relevant dialogs and reset states
        setBmwAddOpen(false);
        setBmwEdit(null); // Fix: Reset bmwEdit to close the edit dialog
        
        setNewBMW({
          date: "", blue_bags: "", blue_weight: "", red_bags: "", red_weight: "",
          white_bags: "", white_weight: "", yellow_bags: "", yellow_weight: "",
          cytotoxic_yellow_bags: "", cytotoxic_yellow_weight: "", covid_bags: "",
          covid_weight: "", sharp_bags: "", sharp_weight: "", seo_title: "",
          meta_title: "", meta_description: "", meta_keywords: "", custom_scripts: "",
          og_title: "", og_description: "", og_type: "", og_url: "", og_image: null,
          og_locale: "", og_site_name: "", og_image_width: "", og_image_height: "",
          og_image_type: "", article_modified_time: "", canonical_link: "",
          twitter_card: "", twitter_title: "", twitter_description: "", twitter_image: null,
          twitter_site: "",
        });
      } else toast.error("Operation failed.");
    } catch {
      toast.error("Error saving data.");
    } finally {
      setLoadingAction(false);
    }
  };
  
  // Handler for BMW PDF file upload (FIXED the 'year' payload)
  const handleBmwPdfUpload = async () => {
    setLoadingAction(true);
    
    // Check for required fields
    if (!bmwPdfData.pdf_file || !bmwPdfData.year || !bmwPdfData.name.trim()) {
        toast.error("Please provide a Name/Title, a Year, and a PDF file.");
        setLoadingAction(false);
        return;
    }
    
    const yearValue = bmwPdfData.year.trim(); // e.g., "2024" (The simple integer year)
    const nameValue = bmwPdfData.name.trim(); // e.g., "Q4 2024 Report - Final" (The flexible name)

    const formData = new FormData();
    
    // FIX: Append the simple year value (e.g., "2024") to the 'year' parameter
    formData.append("year", yearValue);
    
    // Append the flexible 'name' field
    formData.append("name", nameValue); 
    
    // Map the file object to the 'pdf_file' parameter
    formData.append("pdf_file", bmwPdfData.pdf_file);

    try {
        const res = await addUpdateBmwPdfFileCMS(formData); 
        if (res?.status === "true" || res?.success) {
            toast.success("Bio-Medical Waste PDF file uploaded successfully!");
            setBmwPdfUploadOpen(false);
            // Reset PDF state
            const currentYear = new Date().getFullYear().toString();
            setBmwPdfData({ 
              year: currentYear, 
              name: `Annual Bio-Medical Waste Report - ${currentYear}`, 
              pdf_file: null 
            });
        } else {
            toast.error("PDF upload failed. Please check the server response.");
        }
    } catch (e) {
        toast.error("Error uploading PDF data.");
    } finally {
        setLoadingAction(false);
    }
  };

  // FIX: Edit handler now only fetches data and sets bmwEdit. The presence of bmwEdit opens the dialog.
  const handleBMWEdit = async (id) => {
    try {
      const res = await editBioMedicalWasteReportCMS(id);
      if (res?.data) {
        // Convert number strings to numbers for initial state 
        const data = { ...res.data };
        // Ensure data fields are set correctly, especially date
        data.date = data.date ? new Date(data.date).toISOString().split('T')[0] : '';

        // Reset file fields that hold file paths from DB to force re-upload on edit if needed
        // For images, we keep the path string, but the ImageUploadField handles the display
        setBmwEdit(data);
      }
      else toast.error("Failed to load report.");
    } catch {
      toast.error("Error editing report.");
    }
  };

  const handleBMWDelete = async (id) => {
    setLoadingAction(true);
    try {
      const res = await trashBioMedicalWasteReportCMS(id);
      if (res?.status === "true") {
        toast.success("Deleted successfully!");
        setBmwReports((p) => p.filter((r) => r.id !== id));
      } else toast.error("Failed to delete.");
    } catch {
      toast.error("Error deleting report.");
    } finally {
      setLoadingAction(false);
      setDeleteId(null);
    }
  };

  const bmwColumns = [
    { field: "id", headerName: "ID", width: 80 },
    { 
      field: "date", 
      headerName: "Date", 
      width: 150,
      renderCell: (params) => formatDateForDisplay(params.value)
    },
    { field: "blue_bags", headerName: "Blue Bags", width: 120 },
    { field: "blue_weight", headerName: "Blue Weight", width: 120 },
    { field: "yellow_bags", headerName: "Yellow Bags", width: 120 },
    { field: "yellow_weight", headerName: "Yellow Weight", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleBMWEdit(params.row.id)} // This sets bmwEdit to the report object
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => setDeleteId({ type: "bmw", id: params.row.id })}
            sx={{ ml: 1 }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  // ---------------------- ORGAN TRANSPLANT ----------------------
  const [otReports, setOtReports] = useState([]);
  const [otLoading, setOtLoading] = useState(true);
  const [otError, setOtError] = useState(null);
  // State for EDITING (will hold the object being edited, null when not editing)
  const [otEdit, setOtEdit] = useState(null); 
  const [otAddOpen, setOtAddOpen] = useState(false);
  // State for ADDING new report (reset after save)
  const [newOT, setNewOT] = useState({
    date: "",
    uhid_recipient: "",
    organ: "",
    donor_type: "",
    results: "",
    seo_title: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    custom_scripts: "",
    og_title: "",
    og_description: "",
    og_type: "",
    og_url: "",
    og_image: null,
    og_locale: "",
    og_site_name: "",
    og_image_width: "",
    og_image_height: "",
    og_image_type: "",
    article_modified_time: "",
    canonical_link: "",
    twitter_card: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: null,
    twitter_site: "",
  });

  useEffect(() => {
    const fetchOT = async () => {
      try {
        const res = await listOrganTransplantReportCMS();
        if (res?.data) setOtReports(res.data);
        else setOtError("Failed to fetch Organ Transplant Reports");
      } catch {
        setOtError("Error fetching reports.");
      } finally {
        setOtLoading(false);
      }
    };
    fetchOT();
  }, []);

  // Handler for Organ Transplant report add/update
  const handleOTAddOrUpdate = async (id = null) => {
    setLoadingAction(true);
    const formData = new FormData();
    const data = id ? otEdit : newOT;
    
    Object.entries(data).forEach(([key, val]) => {
      if (key === 'og_image' || key === 'twitter_image') {
        if (val instanceof File) {
          formData.append(key, val);
        } else if (typeof val === 'string' && val.includes('/')) {
             formData.append(key, val);
        }
      } else {
        formData.append(key, val || "");
      }
    });

    try {
      const res = id
        ? await updateOrganTransplantReportCMS(data.id, formData)
        : await addOrganTransplantReportCMS(formData);
      if (res?.status === "true" || res?.success) {
        toast.success(id ? "Updated successfully!" : "Added successfully!");
        const updated = await listOrganTransplantReportCMS();
        setOtReports(updated.data || []);
        
        // Close relevant dialogs and reset states
        setOtAddOpen(false);
        setOtEdit(null); // Fix: Reset otEdit to close the edit dialog
        
        setNewOT({
          date: "", uhid_recipient: "", organ: "", donor_type: "", results: "",
          seo_title: "", meta_title: "", meta_description: "", meta_keywords: "",
          custom_scripts: "", og_title: "", og_description: "", og_type: "",
          og_url: "", og_image: null, og_locale: "", og_site_name: "",
          og_image_width: "", og_image_height: "", og_image_type: "",
          article_modified_time: "", canonical_link: "", twitter_card: "",
          twitter_title: "", twitter_description: "", twitter_image: null, twitter_site: "",
        });
      } else toast.error("Operation failed.");
    } catch {
      toast.error("Error saving data.");
    } finally {
      setLoadingAction(false);
    }
  };

  // FIX: Edit handler now only fetches data and sets otEdit. The presence of otEdit opens the dialog.
  const handleOTEdit = async (id) => {
    try {
      const res = await editOrganTransplantReportCMS(id);
      if (res?.data) {
        const data = { ...res.data };
        data.date = data.date ? new Date(data.date).toISOString().split('T')[0] : '';
        setOtEdit(data);
      }
      else toast.error("Failed to load data.");
    } catch {
      toast.error("Error editing record.");
    }
  };

  const handleOTDelete = async (id) => {
    setLoadingAction(true);
    try {
      const res = await trashOrganTransplantReportCMS(id);
      if (res?.status === "true") {
        toast.success("Deleted successfully!");
        setOtReports((p) => p.filter((r) => r.id !== id));
      } else toast.error("Failed to delete.");
    } catch {
      toast.error("Error deleting record.");
    } finally {
      setLoadingAction(false);
      setDeleteId(null);
    }
  };

  const otColumns = [
    { field: "id", headerName: "ID", width: 80 },
    { 
      field: "date", 
      headerName: "Date", 
      width: 150,
      renderCell: (params) => formatDateForDisplay(params.value)
    },
    { field: "uhid_recipient", headerName: "UHID", width: 150 },
    { field: "organ", headerName: "Organ", width: 120 },
    { field: "donor_type", headerName: "Donor Type", width: 150 },
    { field: "results", headerName: "Results", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleOTEdit(params.row.id)} // This sets otEdit to the report object
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => setDeleteId({ type: "ot", id: params.row.id })}
            sx={{ ml: 1 }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  // ---------------------- ORGAN TRANSPLANT - COMMITTEE DATA ----------------------
  const [committees, setCommittees] = useState([]);
  const [committeeLoading, setCommitteeLoading] = useState(true);
  const [committeeError, setCommitteeError] = useState(null);
  // State for EDITING (will hold the object being edited, null when not editing)
  const [committeeEdit, setCommitteeEdit] = useState(null);
  const [committeeAddOpen, setCommitteeAddOpen] = useState(false);
  // State for ADDING new record (reset after save)
  const [newCommittee, setNewCommittee] = useState(EMPTY_COMMITTEE);

  useEffect(() => {
    const fetchCommittees = async () => {
      try {
        const res = await listOrganTransplantCommitteeCMS();
        if (res?.data) setCommittees(res.data);
        else setCommitteeError("Failed to fetch Organ Transplant Committee Data");
      } catch {
        setCommitteeError("Error fetching committee data.");
      } finally {
        setCommitteeLoading(false);
      }
    };
    fetchCommittees();
  }, []);

  // Handler for Committee record add/update
  const handleCommitteeAddOrUpdate = async (id = null) => {
    const data = id ? committeeEdit : newCommittee;

    if (!data.date || !data.committee_type?.trim() || !data.code_no?.trim() || !data.results?.trim()) {
      toast.error("Please fill Date, Type of Committee, Code No and Results.");
      return;
    }

    setLoadingAction(true);
    const formData = new FormData();
    ["hospital", "date", "committee_type", "code_no", "results"].forEach((key) => {
      formData.append(key, (data[key] || "").trim());
    });

    try {
      const res = id
        ? await updateOrganTransplantCommitteeCMS(id, formData)
        : await addOrganTransplantCommitteeCMS(formData);
      if (res?.status === "true") {
        toast.success(id ? "Updated successfully!" : "Added successfully!");
        const updated = await listOrganTransplantCommitteeCMS();
        setCommittees(updated.data || []);

        // Close relevant dialogs and reset states
        setCommitteeAddOpen(false);
        setCommitteeEdit(null);
        setNewCommittee(EMPTY_COMMITTEE);
      } else {
        const firstError = res?.errors ? Object.values(res.errors)[0]?.[0] : null;
        toast.error(firstError || res?.message || "Operation failed.");
      }
    } catch {
      toast.error("Error saving data.");
    } finally {
      setLoadingAction(false);
    }
  };

  // Edit handler only fetches data and sets committeeEdit. The presence of committeeEdit opens the dialog.
  const handleCommitteeEdit = async (id) => {
    try {
      const res = await editOrganTransplantCommitteeCMS(id);
      if (res?.data) {
        const data = { ...res.data };
        data.date = data.date ? String(data.date).slice(0, 10) : "";
        setCommitteeEdit(data);
      }
      else toast.error("Failed to load data.");
    } catch {
      toast.error("Error editing record.");
    }
  };

  const handleCommitteeDelete = async (id) => {
    setLoadingAction(true);
    try {
      const res = await trashOrganTransplantCommitteeCMS(id);
      if (res?.status === "true") {
        toast.success("Deleted successfully!");
        setCommittees((p) => p.filter((r) => r.id !== id));
      } else toast.error("Failed to delete.");
    } catch {
      toast.error("Error deleting record.");
    } finally {
      setLoadingAction(false);
      setDeleteId(null);
    }
  };

  const committeeColumns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "date", headerName: "Date", width: 120 },
    { field: "committee_type", headerName: "Type of Committee", flex: 1, minWidth: 220 },
    { field: "code_no", headerName: "Code No", flex: 1, minWidth: 260 },
    { field: "results", headerName: "Results", width: 110 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => handleCommitteeEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => setDeleteId({ type: "committee", id: params.row.id })}
            sx={{ ml: 1 }}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  // ---------------------- MAIN RENDER ----------------------
  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Regulatory Compliance Reports
      </Typography>

      <Tabs value={tab} onChange={handleTabChange}>
        <Tab label="Bio-Medical Waste Management" />
        <Tab label="Organ Transplant" />
        <Tab label="Organ Transplant - Committee Data" />
      </Tabs>

      {/* ----------- TAB 1: BIO-MEDICAL WASTE ----------- */}
      {tab === 0 && (
        <Box mt={3}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setBmwAddOpen(true)}
              sx={{ mr: 2 }}
            >
              Add New Report
            </Button>
            
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setBmwPdfUploadOpen(true)}
            >
              Upload PDF Report
            </Button>
          </Box>

          {bmwLoading ? (
            <CircularProgress />
          ) : bmwError ? (
            <Alert severity="error">{bmwError}</Alert>
          ) : (
            <DataGrid rows={bmwReports} columns={bmwColumns} autoHeight />
          )}
        </Box>
      )}

      {/* ----------- TAB 2: ORGAN TRANSPLANT ----------- */}
      {tab === 1 && (
        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOtAddOpen(true)}
            sx={{ mb: 2 }}
          >
            Add New Record
          </Button>
          {otLoading ? (
            <CircularProgress />
          ) : otError ? (
            <Alert severity="error">{otError}</Alert>
          ) : (
            <DataGrid rows={otReports} columns={otColumns} autoHeight />
          )}
        </Box>
      )}

      {/* ----------- TAB 3: ORGAN TRANSPLANT - COMMITTEE DATA ----------- */}
      {tab === 2 && (
        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setCommitteeAddOpen(true)}
            sx={{ mb: 2 }}
          >
            Add New Record
          </Button>
          {committeeLoading ? (
            <CircularProgress />
          ) : committeeError ? (
            <Alert severity="error">{committeeError}</Alert>
          ) : (
            <DataGrid rows={committees} columns={committeeColumns} autoHeight />
          )}
        </Box>
      )}

      {/* --------------------- DIALOGS --------------------- */}

      {/* 1. ADD DIALOG: BMW DATA */}
      <Dialog
        open={bmwAddOpen}
        onClose={() => setBmwAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Bio-Medical Waste Report</DialogTitle>
        <DialogContent>
            <BmwFormFields data={newBMW} setData={setNewBMW} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBmwAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleBMWAddOrUpdate()}
            disabled={loadingAction}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 2. EDIT DIALOG: BMW DATA (FIXED: Controlled by bmwEdit state) */}
      <Dialog
        open={!!bmwEdit}
        onClose={() => setBmwEdit(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Bio-Medical Waste Report (ID: {bmwEdit?.id})</DialogTitle>
        <DialogContent>
            {bmwEdit && <BmwFormFields data={bmwEdit} setData={setBmwEdit} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBmwEdit(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleBMWAddOrUpdate(bmwEdit?.id)}
            disabled={loadingAction}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* 3. NEW DIALOG: BMW PDF UPLOAD (FIXED) */}
      <Dialog
        open={bmwPdfUploadOpen}
        onClose={() => setBmwPdfUploadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Bio-Medical Waste PDF Report</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name/Title (Can be any text)" // This is the flexible title
            margin="dense"
            type="text"
            value={bmwPdfData.name}
            onChange={(e) => setBmwPdfData({ ...bmwPdfData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Report Year (Must be an integer, e.g., 2024)" // This is the integer year
            margin="dense"
            type="number"
            value={bmwPdfData.year}
            onChange={(e) => setBmwPdfData({ ...bmwPdfData, year: e.target.value })}
          />
          <Box display="flex" alignItems="center" my={2}>
            <Button variant="contained" component="label" sx={{ minWidth: '150px' }}>
              Select PDF File
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => setBmwPdfData({ ...bmwPdfData, pdf_file: e.target.files[0] })}
              />
            </Button>
            <Typography variant="body2" sx={{ ml: 2 }}>
              {bmwPdfData.pdf_file ? bmwPdfData.pdf_file.name : "No file selected"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBmwPdfUploadOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBmwPdfUpload}
            // Ensure year is numeric and non-empty for validation
            disabled={loadingAction || !bmwPdfData.pdf_file || !bmwPdfData.year || isNaN(bmwPdfData.year) || !bmwPdfData.name.trim()}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Upload PDF"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* 4. ADD DIALOG: OT DATA */}
      <Dialog
        open={otAddOpen}
        onClose={() => setOtAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Organ Transplant Report</DialogTitle>
        <DialogContent>
            <OtFormFields data={newOT} setData={setNewOT} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleOTAddOrUpdate()}
            disabled={loadingAction}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 5. EDIT DIALOG: OT DATA (FIXED: Controlled by otEdit state) */}
      <Dialog
        open={!!otEdit}
        onClose={() => setOtEdit(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Organ Transplant Report (ID: {otEdit?.id})</DialogTitle>
        <DialogContent>
            {otEdit && <OtFormFields data={otEdit} setData={setOtEdit} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtEdit(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleOTAddOrUpdate(otEdit?.id)}
            disabled={loadingAction}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 6. ADD DIALOG: COMMITTEE DATA */}
      <Dialog
        open={committeeAddOpen}
        onClose={() => setCommitteeAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Organ Transplant Committee Record</DialogTitle>
        <DialogContent>
            <CommitteeFormFields data={newCommittee} setData={setNewCommittee} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommitteeAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleCommitteeAddOrUpdate()}
            disabled={loadingAction}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 7. EDIT DIALOG: COMMITTEE DATA (Controlled by committeeEdit state) */}
      <Dialog
        open={!!committeeEdit}
        onClose={() => setCommitteeEdit(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Organ Transplant Committee Record (ID: {committeeEdit?.id})</DialogTitle>
        <DialogContent>
            {committeeEdit && <CommitteeFormFields data={committeeEdit} setData={setCommitteeEdit} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommitteeEdit(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handleCommitteeAddOrUpdate(committeeEdit?.id)}
            disabled={loadingAction}
          >
            {loadingAction ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ----------- DELETE CONFIRMATION ----------- */}
      {deleteId && (
        <Dialog open onClose={() => setDeleteId(null)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this record?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                deleteId.type === "bmw"
                  ? handleBMWDelete(deleteId.id)
                  : deleteId.type === "committee"
                    ? handleCommitteeDelete(deleteId.id)
                    : handleOTDelete(deleteId.id)
              }
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default RegulatoryCompliance;
